import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus, Users, Wallet, Coins, AlertCircle, Edit2, Trash2, ChevronRight, UserX
} from 'lucide-react';
import api from '../config/api';
import { useLanguage } from '../context/LanguageContext';
import { showNotification } from '../components/GlobalNotification';
import AddStaffModal from '../components/AddStaffModal';
import StaffTransactionModal from '../components/StaffTransactionModal';
import StaffLedgerModal from '../components/StaffLedgerModal';

function SummaryCard({ icon: Icon, iconBg, iconColor, label, value, hint }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
      <div className="flex items-center">
        <div className={`p-3 rounded-lg flex-shrink-0 ${iconBg}`}>
          <Icon className={`h-6 w-6 ${iconColor}`} />
        </div>
        <div className="ml-4 min-w-0">
          <h3 className="text-sm font-medium text-gray-500 truncate">{label}</h3>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{value}</p>
          {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
        </div>
      </div>
    </div>
  );
}

function Staff() {
  const { t } = useLanguage();
  const [staffList, setStaffList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // Modal state
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);

  const [ledgerModalOpen, setLedgerModalOpen] = useState(false);
  const [ledgerStaff, setLedgerStaff] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const [txModalOpen, setTxModalOpen] = useState(false);
  const [txStaff, setTxStaff] = useState(null);
  const [editingTx, setEditingTx] = useState(null);

  const fetchStaff = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const [listRes, summaryRes] = await Promise.all([
        api.get('/api/staff', { params: { includeInactive: showInactive } }),
        api.get('/api/staff/summary')
      ]);

      setStaffList(listRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch staff');
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  // Reload the open ledger so its balance and history stay in step with edits
  const refreshLedger = async (staffId) => {
    const response = await api.get(`/api/staff/${staffId}`);
    setLedgerStaff(response.data);
    return response.data;
  };

  const handleSaveStaff = async (formData) => {
    try {
      if (editingStaff) {
        await api.put(`/api/staff/${editingStaff._id}`, formData);
        showNotification('success', t('Staff member updated successfully!'));
      } else {
        await api.post('/api/staff', formData);
        showNotification('success', t('Staff member added successfully!'));
      }

      setStaffModalOpen(false);
      setEditingStaff(null);
      await fetchStaff();
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to save staff member');
    }
  };

  const handleDeleteStaff = async (staff) => {
    const confirmed = window.confirm(
      t('Delete {name}?\n\nTheir entire account history will be removed, including the expense entries created from their advances and salary payments. This cannot be undone.', { name: staff.name })
    );
    if (!confirmed) return;

    try {
      await api.delete(`/api/staff/${staff._id}`);
      showNotification('success', t('{name} deleted successfully!', { name: staff.name }));
      await fetchStaff();
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to delete staff member');
    }
  };

  const handleOpenLedger = async (staff) => {
    setLedgerModalOpen(true);
    setLedgerLoading(true);
    setLedgerStaff(null);

    try {
      await refreshLedger(staff._id);
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to load account');
    } finally {
      setLedgerLoading(false);
    }
  };

  const handleOpenTransaction = (staff, transaction = null) => {
    setTxStaff(staff);
    setEditingTx(transaction);
    setTxModalOpen(true);
  };

  const handleSaveTransaction = async (formData) => {
    try {
      if (editingTx) {
        await api.put(`/api/staff/transactions/${editingTx._id}`, formData);
        showNotification('success', t('Entry updated successfully!'));
      } else {
        await api.post(`/api/staff/${txStaff._id}/transactions`, formData);
        showNotification('success', t('Entry recorded successfully!'));
      }

      setTxModalOpen(false);
      setEditingTx(null);

      // The ledger modal may be sitting behind this one showing stale numbers
      if (ledgerModalOpen && ledgerStaff) {
        await refreshLedger(ledgerStaff._id);
      }
      await fetchStaff();
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to save entry');
    }
  };

  const handleDeleteTransaction = async (tx) => {
    if (!window.confirm(t('Delete this entry? The matching expense entry is removed too.'))) {
      return;
    }

    try {
      await api.delete(`/api/staff/transactions/${tx._id}`);
      showNotification('success', t('Entry deleted successfully!'));

      if (ledgerStaff) {
        await refreshLedger(ledgerStaff._id);
      }
      await fetchStaff();
    } catch (err) {
      showNotification('error', err.response?.data?.message || 'Failed to delete entry');
    }
  };

  return (
    <div className="space-y-4 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        <div className="min-w-0">
          <h2 className="text-2xl font-bold text-gray-900">{t('Staff Accounts')}</h2>
          <p className="text-sm sm:text-base text-gray-600">
            {t('Track salaries, advances and what each staff member is owed')}
          </p>
        </div>
        <div className="flex gap-2 sm:gap-3 flex-shrink-0">
          <button
            onClick={() => setShowInactive(!showInactive)}
            className={`flex-1 sm:flex-none px-4 py-2 min-h-[44px] whitespace-nowrap rounded-lg transition-colors flex items-center justify-center ${
              showInactive
                ? 'bg-gray-700 text-white hover:bg-gray-800'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-200'
            }`}
          >
            <UserX className="h-4 w-4 mr-2 flex-shrink-0" />
            {showInactive ? 'Hide Former' : 'Show Former'}
          </button>
          <button
            onClick={() => {
              setEditingStaff(null);
              setStaffModalOpen(true);
            }}
            className="flex-1 sm:flex-none px-4 sm:px-6 py-2 min-h-[44px] whitespace-nowrap bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
          >
            <Plus className="h-5 w-5 mr-2 flex-shrink-0" />
            {t('Add Staff')}
          </button>
        </div>
      </div>

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6">
          <SummaryCard
            icon={Users}
            iconBg="bg-blue-100"
            iconColor="text-blue-600"
            label="Staff"
            value={summary.activeCount}
            hint={
              summary.staffCount > summary.activeCount
                ? `${summary.staffCount - summary.activeCount} former`
                : 'currently working'
            }
          />
          <SummaryCard
            icon={Wallet}
            iconBg="bg-purple-100"
            iconColor="text-purple-600"
            label={t('Monthly Payroll')}
            value={`Rs. ${summary.totalMonthlySalary.toFixed(2)}`}
            hint="active staff only"
          />
          <SummaryCard
            icon={AlertCircle}
            iconBg="bg-green-100"
            iconColor="text-green-600"
            label={t('You Owe')}
            value={`Rs. ${summary.totalPayable.toFixed(2)}`}
            hint="unpaid earnings"
          />
          <SummaryCard
            icon={Coins}
            iconBg="bg-orange-100"
            iconColor="text-orange-600"
            label={t('Paid Ahead')}
            value={`Rs. ${summary.totalReceivable.toFixed(2)}`}
            hint="advances not yet earned"
          />
        </div>
      )}

      {/* Staff list */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('Staff')}</h3>

        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('Loading staff...')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <AlertCircle className="h-8 w-8 text-red-500 mx-auto mb-4" />
            <p className="text-red-600">{error}</p>
          </div>
        ) : staffList.length === 0 ? (
          <div className="text-center py-8">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">{t('No Staff Yet')}</h4>
            <p className="text-gray-500 mb-4">{t('Add your staff to start tracking salaries and advances.')}</p>
            <button
              onClick={() => {
                setEditingStaff(null);
                setStaffModalOpen(true);
              }}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('Add First Staff Member')}
            </button>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-lg divide-y divide-gray-200 overflow-hidden">
            {staffList.map((staff) => {
              const balance = staff.ledger?.balance ?? 0;

              return (
                <div
                  key={staff._id}
                  className="p-4 hover:bg-gray-50 transition-colors flex flex-col sm:flex-row sm:items-center gap-3"
                >
                  <button
                    onClick={() => handleOpenLedger(staff)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 truncate">{staff.name}</span>
                      {!staff.isActive && (
                        <span className="bg-gray-200 text-gray-700 px-2 py-0.5 rounded-full text-xs flex-shrink-0">
                          {t('Former')}
                        </span>
                      )}
                      <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>
                    <div className="text-sm text-gray-600 mt-0.5">
                      {staff.position && <span>{staff.position} • </span>}
                      <span>Rs. {staff.monthlySalary.toFixed(2)}/month</span>
                      {staff.phoneNumber && <span className="hidden sm:inline"> • {staff.phoneNumber}</span>}
                    </div>
                  </button>

                  <div className="flex items-center justify-between sm:justify-end gap-2 sm:gap-4 flex-shrink-0">
                    <div className="text-left sm:text-right">
                      <p className="text-xs text-gray-500">
                        {balance > 0 ? 'You owe' : balance < 0 ? 'Paid ahead' : 'Settled'}
                      </p>
                      <p
                        className={`text-base font-semibold ${
                          balance > 0 ? 'text-green-700' : balance < 0 ? 'text-red-700' : 'text-gray-900'
                        }`}
                      >
                        Rs. {Math.abs(balance).toFixed(2)}
                      </p>
                    </div>

                    <div className="flex gap-1">
                      <button
                        onClick={() => handleOpenTransaction(staff)}
                        className="px-3 py-2 min-h-[40px] text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center whitespace-nowrap"
                        title={t('Record advance, salary or bonus')}
                      >
                        <Plus className="h-4 w-4 sm:mr-1" />
                        <span className="hidden sm:inline">{t('Entry')}</span>
                      </button>
                      <button
                        onClick={() => {
                          setEditingStaff(staff);
                          setStaffModalOpen(true);
                        }}
                        className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                        title={t('Edit staff member')}
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteStaff(staff)}
                        className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        title={t('Delete staff member')}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modals */}
      <AddStaffModal
        isOpen={staffModalOpen}
        onClose={() => {
          setStaffModalOpen(false);
          setEditingStaff(null);
        }}
        onSubmit={handleSaveStaff}
        staff={editingStaff}
      />

      <StaffLedgerModal
        isOpen={ledgerModalOpen}
        onClose={() => {
          setLedgerModalOpen(false);
          setLedgerStaff(null);
        }}
        staff={ledgerStaff}
        loading={ledgerLoading}
        onAddTransaction={() => handleOpenTransaction(ledgerStaff)}
        onEditTransaction={(tx) => handleOpenTransaction(ledgerStaff, tx)}
        onDeleteTransaction={handleDeleteTransaction}
      />

      <StaffTransactionModal
        isOpen={txModalOpen}
        onClose={() => {
          setTxModalOpen(false);
          setEditingTx(null);
        }}
        onSubmit={handleSaveTransaction}
        staff={txStaff}
        transaction={editingTx}
      />
    </div>
  );
}

export default Staff;
